/**
 * TBB Creator Time Capsule / Spotlight — Auto-rotation + Navigation
 *
 * Scoped to [data-tbb-capsule] containers rendered by d_image-with-text.liquid
 * when capsule_mode is ON. Handles:
 *   1. Timed crossfade between .tbb-capsule-slide elements
 *   2. Navigation dot click → jump to slide
 *   3. Prev/next arrow buttons
 *   4. Touch swipe (left/right) on mobile
 *   5. Pause on hover / focus (a11y)
 *   6. Resume on mouse-leave / blur
 *
 * No dependencies. Idempotent (data-tbb-capsule-init flag).
 */
(function () {
  'use strict';

  /**
   * Inner image rotation — each slide can have a `.tbb-capsule-img-track`
   * with 2-4 stacked `<img class="tbb-capsule-img-slide">`. Cycles them
   * by toggling `is-active` / `is-leaving` classes at the same speed as
   * the outer slide rotation. Pauses when the capsule is hovered/focused
   * (shares the outer's `paused` flag via closure).
   */
  function initImageTrack(track, speed, isPaused) {
    var slides = track.querySelectorAll('.tbb-capsule-img-slide');
    if (slides.length < 2) return null;
    var current = 0;
    var timer = setInterval(function () {
      if (isPaused()) return;
      var prev = current;
      current = (current + 1) % slides.length;
      slides[prev].classList.remove('is-active');
      slides[prev].classList.add('is-leaving');
      slides[current].classList.add('is-active');
      // Reset the leaving slide back to the off-right parked position
      // after the transition completes, so it can re-enter cleanly next loop.
      setTimeout(function () {
        slides[prev].classList.remove('is-leaving');
      }, 600);
    }, speed);
    return timer;
  }

  function initCapsule(container) {
    if (container.dataset.tbbCapsuleInit) return;
    container.dataset.tbbCapsuleInit = '1';

    var slides = container.querySelectorAll('.tbb-capsule-slide');
    var dots = container.querySelectorAll('.tbb-capsule-dot');
    var prevBtn = container.querySelector('[data-capsule-prev]');
    var nextBtn = container.querySelector('[data-capsule-next]');
    var count = slides.length;

    var speed = (parseInt(container.dataset.rotationSpeed, 10) || 8) * 1000;
    var current = 0;
    var timer = null;
    var paused = false;

    // Set up inner image-track rotation for each slide that has 2+ images.
    // Runs independently of outer slide rotation; pauses on the same
    // hover/focus events.
    var imageTimers = [];
    container.querySelectorAll('[data-tbb-capsule-img-track]').forEach(function (track) {
      var t = initImageTrack(track, speed, function () { return paused; });
      if (t) imageTimers.push(t);
    });

    if (count < 2) return; // Outer rotation only needed for 2+ slides

    function goTo(index) {
      if (index === current) return;
      slides[current].classList.remove('is-active');
      if (dots[current]) dots[current].classList.remove('is-active');
      current = index;
      slides[current].classList.add('is-active');
      if (dots[current]) dots[current].classList.add('is-active');
    }

    function next() {
      goTo((current + 1) % count);
    }

    function prev() {
      goTo((current - 1 + count) % count);
    }

    function startTimer() {
      if (timer) clearInterval(timer);
      timer = setInterval(function () {
        if (!paused) next();
      }, speed);
    }

    // Dot click handlers
    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var idx = parseInt(dot.dataset.capsuleDot, 10);
        if (!isNaN(idx)) {
          goTo(idx);
          startTimer();
        }
      });
    });

    // Arrow click handlers
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        prev();
        startTimer();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        next();
        startTimer();
      });
    }

    // Touch swipe support
    var touchStartX = 0;
    var touchEndX = 0;
    var SWIPE_THRESHOLD = 50;

    container.addEventListener('touchstart', function (e) {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    container.addEventListener('touchend', function (e) {
      touchEndX = e.changedTouches[0].screenX;
      var diff = touchStartX - touchEndX;
      if (Math.abs(diff) > SWIPE_THRESHOLD) {
        if (diff > 0) {
          next(); // Swipe left → next
        } else {
          prev(); // Swipe right → prev
        }
        startTimer();
      }
    }, { passive: true });

    // Pause on hover / focus
    container.addEventListener('mouseenter', function () { paused = true; });
    container.addEventListener('mouseleave', function () { paused = false; });
    container.addEventListener('focusin', function () { paused = true; });
    container.addEventListener('focusout', function () { paused = false; });

    // Kick off
    startTimer();
  }

  function initAll() {
    document.querySelectorAll('[data-tbb-capsule]').forEach(initCapsule);
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Also watch for Shopify section rendering (theme editor live preview)
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var capsule = e.target.querySelector('[data-tbb-capsule]');
      if (capsule) {
        capsule.removeAttribute('data-tbb-capsule-init');
        initCapsule(capsule);
      }
    });
  }
})();
